import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import SubmitButton from '../../../components/Common/SubmitButton';
import { saveAdminSettingsService } from './services/AdminForms.service';
import { PlanDetailsModel } from '../../../interfaces/plan.interface';

interface SettingFormProps {
  setShowForm: (value: boolean) => void;
  getAllSettings: () => void;
  settingDetails: Array<any>;
  plans: PlanDetailsModel[];
}

export type EditSettingInputType = 'input' | 'select';

export const SettingForm = ({ setShowForm, getAllSettings, settingDetails, plans = [] }: SettingFormProps) => {
  const [loader, setLoader] = useState<boolean>(false);
  const [inputType, setInputType] = useState<EditSettingInputType>('input');
  const [selectList, setSelectList] = useState<{ title: string; value: string }[]>([]);
  const validationSchema = Yup.object().shape({
    settingKey: Yup.string().required('Setting Key is required'),
    settingValue: Yup.string().required('Setting Value is required'),
  });
  const formik = useFormik({
    initialValues: {
      settingKey: '',
      settingTitle: '',
      settingValue: '',
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      handleSubmit();
    },
  });

  const handleSubmit = async () => {
    await saveAdminSettingsService(formik.values, setLoader, setShowForm, getAllSettings);
  };

  useEffect(() => {
    if (settingDetails) {
      const settingsData = settingDetails[0];
      if (typeof settingsData?.settingValue === 'boolean') {
        setInputType('select');
        setSelectList([
          { title: 'True', value: 'true' },
          { title: 'False', value: 'false' },
        ]);
      }
      if (settingsData.settingValueType) {
        setInputType(settingsData.settingValueType);
        if (settingsData.settingValueOptionsType === 'plans') {
          setSelectList(plans?.map((item: any) => ({ title: `${item.name} (${item.id})`, value: item.id })) || []);
        } else {
          setSelectList(
            settingsData.settingValueList?.map((item: string | { title: string; value: string }) => {
              if (typeof item === 'string') {
                return {
                  title: item,
                  value: item,
                };
              }
              return {
                title: item.title,
                value: item.value,
              };
            }) || [],
          );
        }
      }
      const values = {
        settingKey: settingsData?.id,
        settingTitle: settingsData?.settingTitle,
        settingValue: settingsData?.settingValue,
      };
      formik.setValues(values);
    }
  }, [settingDetails]);
  return (
    <div className="bg-storytan mx-auto">
      <div className="max-w-7xl mx-auto min-h-screen">
        <h1 className="text-storyblue text-xl font-bold text-start mb-4">Edit Setting</h1>
        <form id="form-setting" name="form-setting" className="text-start" onSubmit={formik.handleSubmit}>
          <div className="flex flex-col gap-2">
            <label className="font-medium">Setting Key</label>
            <input
              type="text"
              name="settingKey"
              value={formik.values.settingKey}
              onChange={formik.handleChange}
              placeholder="Setting Key"
              className="p-2 w-full rounded-md text-[#9EA4B0]"
              readOnly
            />
            <div className="text-red-600 mt-1">{formik.errors.settingKey}</div>
          </div>
          <div className="flex flex-col gap-2">
            <label className="font-medium">Setting Title</label>
            <input
              type="text"
              name="settingTitle"
              value={formik.values.settingTitle}
              onChange={formik.handleChange}
              placeholder="Setting Title"
              className="p-2 w-full rounded-md"
              autoFocus={true}
            />
            <div className="text-red-600 mt-1">{formik.errors.settingTitle}</div>
          </div>
          <div className="flex flex-col gap-2">
            <label className="font-medium">Setting Value</label>

            {inputType === 'select' ? (
              <select
                id="settingValue"
                name="settingValue"
                className="p-2 w-full rounded-md"
                value={formik.values.settingValue}
                onChange={formik.handleChange}
              >
                <option value="">Please Select</option>
                {selectList.map((item, index) => (
                  <option key={index} value={item?.value} className="capitalize">
                    {item?.title}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                name="settingValue"
                value={formik.values.settingValue}
                onChange={formik.handleChange}
                placeholder="Setting Value"
                className="p-2 w-full rounded-md"
              />
            )}
            <div className="text-red-600 mt-1">{formik.errors.settingValue}</div>
          </div>
          <SubmitButton title="Save" loader={loader} cancelOperation={setShowForm} />
        </form>
      </div>
    </div>
  );
};
